import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  CreditCard,
  Banknote,
  Building2,
  Trash2,
  Plus,
  Euro,
  Calendar,
  CheckCircle2,
  Clock,
  AlertCircle,
  Loader2,
  FileText
} from "lucide-react";
import { currency, formatDate } from "@/utils/helpers";
import { cn } from "@/lib/utils";

const PAYMENT_METHODS = [
  { value: "Bonifico", label: "Bonifico Bancario", icon: Building2, hasReceipt: false },
  { value: "POS", label: "POS / Carta (con quietanza)", icon: CreditCard, hasReceipt: true },
  { value: "Cassa", label: "Cassa / Contanti (con quietanza)", icon: Banknote, hasReceipt: true },
  { value: "Assegno", label: "Assegno Bancario", icon: Euro, hasReceipt: false },
  { value: "RimessaDiretta", label: "Rimessa diretta / presentazione fattura", icon: FileText, hasReceipt: false },
  { value: "Altro", label: "Altro", icon: Euro, hasReceipt: false },
];

/**
 * Payment Dialog Component
 * Manages partial/full payments for an invoice
 */
export default function PaymentDialog({
  open,
  onOpenChange,
  invoice,
  onAddPayment,
  onRemovePayment,
}) {
  const [isAdding, setIsAdding] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [removingIndex, setRemovingIndex] = useState(null);
  
  // New payment form state
  const [newPayment, setNewPayment] = useState({
    amount: "",
    paymentDate: new Date().toISOString().split("T")[0],
    paymentMethod: "Bonifico",
    notes: "",
    includeReceipt: false,
  });

  // Calculated values - include stamp duty if not already in totale
  const speseArt15 = invoice?.speseArt15 || 0;
  const bolloSalvato = invoice?.bolloAutomatico || 0;
  
  // For legacy invoices, check if bollo should have been applied but wasn't
  const bolloMancante = (speseArt15 > 77.47 && bolloSalvato === 0) ? 2.00 : 0;
  
  // Use corrected total (original + missing bollo if applicable)
  const totaleOriginale = invoice?.totale || 0;
  const totaleCorretto = totaleOriginale + bolloMancante;
  
  const totalPaid = invoice?.totalPaid || 0;
  // Always calculate remaining balance using corrected total
  const remainingBalance = totaleCorretto - totalPaid;
  const payments = invoice?.payments || [];
  const paymentStatus = invoice?.paymentStatus || "non_pagata";

  // Reset form when closing
  const handleOpenChange = (isOpen) => {
    if (!isOpen) {
      setIsAdding(false);
      setNewPayment({
        amount: "",
        paymentDate: new Date().toISOString().split("T")[0],
        paymentMethod: "Bonifico",
        notes: "",
        includeReceipt: false,
      });
    }
    onOpenChange(isOpen);
  };

  // Handle adding new payment
  const handleAddPayment = async () => {
    const amount = parseFloat(newPayment.amount);
    if (!amount || amount <= 0) {
      alert("Inserisci un importo valido");
      return;
    }
    
    if (!newPayment.paymentDate) {
      alert("Inserisci una data di pagamento");
      return;
    }

    setIsSubmitting(true);
    try {
      await onAddPayment(invoice.id, {
        amount,
        paymentDate: newPayment.paymentDate,
        paymentMethod: newPayment.paymentMethod,
        notes: newPayment.notes,
        includeReceipt: newPayment.includeReceipt,
      });
      
      // Reset form after successful add
      setIsAdding(false);
      setNewPayment({
        amount: "",
        paymentDate: new Date().toISOString().split("T")[0],
        paymentMethod: "Bonifico",
        notes: "",
        includeReceipt: false,
      });
    } catch (error) {
      alert(error.message || "Errore durante l'aggiunta del pagamento");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle removing payment
  const handleRemovePayment = async (index) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo pagamento?")) {
      return;
    }
    
    setRemovingIndex(index);
    try {
      await onRemovePayment(invoice.id, index);
    } catch (error) {
      alert(error.message || "Errore durante l'eliminazione del pagamento");
    } finally {
      setRemovingIndex(null);
    }
  };

  // Set amount to remaining balance
  const setAmountToRemaining = () => {
    setNewPayment(prev => ({
      ...prev,
      amount: remainingBalance.toFixed(2)
    }));
  };

  // Get payment method icon
  const getMethodIcon = (method) => {
    const methodConfig = PAYMENT_METHODS.find(m => m.value === method);
    return methodConfig?.icon || Euro;
  };

  // Get status badge
  const getStatusBadge = () => {
    switch (paymentStatus) {
      case "pagata":
        return (
          <Badge className="bg-green-100 text-green-800 border-green-300">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Completamente Pagata
          </Badge>
        );
      case "parziale":
        return (
          <Badge className="bg-amber-100 text-amber-800 border-amber-300">
            <Clock className="h-3 w-3 mr-1" />
            Parzialmente Pagata
          </Badge>
        );
      default:
        return (
          <Badge className="bg-red-100 text-red-800 border-red-300">
            <AlertCircle className="h-3 w-3 mr-1" />
            Non Pagata
          </Badge>
        );
    }
  };

  if (!invoice) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Euro className="h-5 w-5 text-blue-600" />
            Gestione Pagamenti
          </DialogTitle>
          <DialogDescription>
            Fattura {invoice.invoiceNumber} - {formatDate(invoice.invoiceDate)}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-4">
          {/* Summary Section */}
          <div className="bg-slate-50 rounded-lg p-4 border">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-slate-600">Stato Pagamento</span>
              {getStatusBadge()}
            </div>
            
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-xs text-slate-500 mb-1">Totale Fattura</p>
                <p className="text-lg font-bold text-slate-900">{currency(totaleCorretto)}</p>
                {bolloMancante > 0 && (
                  <p className="text-xs text-amber-600">(incl. bollo €2)</p>
                )}
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Già Pagato</p>
                <p className={cn(
                  "text-lg font-bold",
                  totalPaid > 0 ? "text-green-600" : "text-slate-400"
                )}>
                  {currency(totalPaid)}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Residuo</p>
                <p className={cn(
                  "text-lg font-bold",
                  remainingBalance > 0 ? "text-amber-600" : "text-green-600"
                )}>
                  {currency(remainingBalance)}
                </p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Payments List */}
          <div className="flex-1 overflow-hidden">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-slate-700">
                Storico Pagamenti ({payments.length})
              </h4>
              <div className={cn(!isAdding && remainingBalance > 0 ? "" : "hidden")}>
                <Button
                  size="sm"
                  onClick={() => setIsAdding(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="add-payment-btn"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Nuovo Pagamento
                </Button>
              </div>
            </div>

            <ScrollArea className="h-[200px] pr-4">
              {/* New Payment Form */}
              <div className={cn(
                "bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3 space-y-4",
                isAdding ? "" : "hidden"
              )}>
                <div className="flex items-center justify-between">
                  <h5 className="font-medium text-blue-800">Nuovo Pagamento</h5>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsAdding(false)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    Annulla
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="payment-amount" className="text-xs">
                      Importo
                    </Label>
                    <div className="relative">
                      <Euro className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
                      <Input
                        id="payment-amount"
                        type="number"
                        step="0.01"
                        min="0.01"
                        max={remainingBalance}
                        placeholder="0.00"
                        value={newPayment.amount}
                        onChange={(e) => setNewPayment(prev => ({ ...prev, amount: e.target.value }))}
                        className="pl-8"
                        data-testid="payment-amount-input"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="p-0 h-auto text-xs text-blue-600"
                      onClick={setAmountToRemaining}
                    >
                      Usa importo residuo ({currency(remainingBalance)})
                    </Button>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="payment-date" className="text-xs">
                      Data Pagamento
                    </Label>
                    <div className="relative">
                      <Calendar className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
                      <Input
                        id="payment-date"
                        type="date"
                        value={newPayment.paymentDate}
                        onChange={(e) => setNewPayment(prev => ({ ...prev, paymentDate: e.target.value }))}
                        className="pl-8"
                        data-testid="payment-date-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="payment-method" className="text-xs">
                    Metodo di Pagamento
                  </Label>
                  <Select
                    value={newPayment.paymentMethod}
                    onValueChange={(value) => {
                      const method = PAYMENT_METHODS.find(m => m.value === value);
                      setNewPayment(prev => ({ 
                        ...prev, 
                        paymentMethod: value,
                        includeReceipt: method?.hasReceipt ? prev.includeReceipt : false
                      }));
                    }}
                  >
                    <SelectTrigger id="payment-method" data-testid="payment-method-select">
                      <SelectValue placeholder="Seleziona metodo" />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map(m => {
                        const MIcon = m.icon;
                        return (
                          <SelectItem key={m.value} value={m.value}>
                            <div className="flex items-center gap-2">
                              <MIcon className="h-4 w-4" />
                              {m.label}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Receipt (Quietanza) option - shown for cash/POS payments */}
                {PAYMENT_METHODS.find(m => m.value === newPayment.paymentMethod)?.hasReceipt && (
                  <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <input
                      type="checkbox"
                      id="include-receipt"
                      checked={newPayment.includeReceipt}
                      onChange={(e) => setNewPayment(prev => ({ ...prev, includeReceipt: e.target.checked }))}
                      className="h-4 w-4 rounded border-green-300 text-green-600 focus:ring-green-500"
                      data-testid="include-receipt-checkbox"
                    />
                    <div>
                      <label htmlFor="include-receipt" className="text-sm font-medium text-green-800 cursor-pointer">
                        Includi quietanza in fattura
                      </label>
                      <p className="text-xs text-green-700">
                        Conferma pagamento ricevuto con quietanza liberatoria
                      </p>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="payment-notes" className="text-xs">
                    Note (opzionale)
                  </Label>
                  <Textarea
                    id="payment-notes"
                    placeholder="Es: Acconto, Saldo, Riferimento bonifico..."
                    value={newPayment.notes}
                    onChange={(e) => setNewPayment(prev => ({ ...prev, notes: e.target.value }))}
                    rows={2}
                    className="resize-none"
                    data-testid="payment-notes-input"
                  />
                </div>

                <Button
                  onClick={handleAddPayment}
                  disabled={isSubmitting}
                  className="w-full bg-green-600 hover:bg-green-700"
                  data-testid="confirm-payment-btn"
                >
                  <div className={cn(isSubmitting ? "hidden" : "flex items-center")}>
                    <Plus className="h-4 w-4 mr-1" />
                    Registra Pagamento
                  </div>
                  <div className={cn(isSubmitting ? "flex items-center" : "hidden")}>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    Registrazione...
                  </div>
                </Button>
              </div>

              {/* Payment History List */}
              <div className={cn(payments.length === 0 ? "" : "hidden")}>
                <div className="text-center py-8 text-slate-400">
                  <Banknote className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Nessun pagamento registrato</p>
                </div>
              </div>

              <div className="space-y-2">
                {payments.map((payment, index) => {
                  const MethodIcon = getMethodIcon(payment.paymentMethod);
                  return (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-white border rounded-lg hover:bg-slate-50 transition-colors"
                      data-testid={`payment-item-${index}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-slate-100 rounded-full">
                          <MethodIcon className="h-4 w-4 text-slate-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-green-700">
                              {currency(payment.amount)}
                            </span>
                            <Badge variant="outline" className="text-xs">
                              {payment.paymentMethod}
                            </Badge>
                          </div>
                          <div className="text-xs text-slate-500">
                            {formatDate(payment.paymentDate)}
                            {payment.notes && (
                              <span className="ml-2 italic">- {payment.notes}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemovePayment(index)}
                        disabled={removingIndex === index}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        data-testid={`remove-payment-${index}`}
                      >
                        <div className={cn(removingIndex === index ? "hidden" : "")}>
                          <Trash2 className="h-4 w-4" />
                        </div>
                        <div className={cn(removingIndex === index ? "" : "hidden")}>
                          <Loader2 className="h-4 w-4 animate-spin" />
                        </div>
                      </Button>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </div>
        </div>

        <DialogFooter className="border-t pt-4">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
          >
            Chiudi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
